/**
 * LIORA — comportements front des sections sur-mesure.
 * 1. Apparitions au scroll (respecte prefers-reduced-motion)
 * 2. <liora-eta> : date de livraison estimee, calculee en jours ouvres
 * 3. <liora-slider> : carrousel avec fleches, reperes et defilement automatique
 * 4. Filtrage des caracteres non gravables dans les champs de personnalisation
 * 5. <liora-readmore> : repli des descriptions longues, frappe au depliage
 * 6. <morrila-bundle> : le format choisi pilote la quantite ajoutee au panier
 * 7. <liora-foot-cols> : colonnes de pied de page, repliables en mobile
 */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* -------------------------------------------------------------------------- */
/* 1. Apparitions                                                             */
/* -------------------------------------------------------------------------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

function initReveals(root = document) {
  const targets = root.querySelectorAll('[data-liora-reveal]:not(.is-revealed)');

  targets.forEach((target, index) => {
    if (prefersReducedMotion.matches || !revealObserver) {
      target.classList.add('is-revealed');
      return;
    }
    if (!target.style.getPropertyValue('--liora-delay')) {
      const step = Number(target.dataset.lioraReveal) || index % 6;
      target.style.setProperty('--liora-delay', `${Math.min(step * 70, 420)}ms`);
    }
    revealObserver.observe(target);
  });
}

/* -------------------------------------------------------------------------- */
/* 2. Date de livraison estimee                                                */
/* -------------------------------------------------------------------------- */

const DAY_MS = 86400000;

/** Ajoute un nombre de jours ouvres (samedi et dimanche exclus) a une date. */
function addBusinessDays(date, days) {
  const result = new Date(date.getTime());
  let remaining = Math.max(0, days);

  while (remaining > 0) {
    result.setTime(result.getTime() + DAY_MS);
    const weekday = result.getDay();
    if (weekday !== 0 && weekday !== 6) remaining -= 1;
  }
  return result;
}

class LioraEta extends HTMLElement {
  connectedCallback() {
    const output = this.querySelector('[data-eta-range]');
    if (!output) return;

    const prep = Number(this.dataset.prepDays ?? 2);
    const min = Number(this.dataset.shipMin ?? 4);
    const max = Number(this.dataset.shipMax ?? 8);
    const cutoff = Number(this.dataset.cutoffHour ?? 15);
    const locale = this.dataset.locale || 'fr-FR';
    const isNumeric = this.dataset.dateStyle === 'numeric';

    const now = new Date();
    // Passee l'heure limite, la mise en fabrication demarre le jour ouvre suivant.
    const start = now.getHours() >= cutoff ? addBusinessDays(now, 1) : now;

    const from = addBusinessDays(start, prep + min);
    const to = addBusinessDays(start, prep + max);

    if (isNumeric) {
      const short = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' });
      output.textContent = `${short.format(from)} et le ${short.format(to)}`;
    } else {
      const dayMonth = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' });
      const dayOnly = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric' });
      const sameMonth = from.getMonth() === to.getMonth();
      output.textContent = `${sameMonth ? dayOnly.format(from) : dayMonth.format(from)} et le ${dayMonth.format(to)}`;
    }

    this.querySelector('[data-eta-cutoff]')?.toggleAttribute('hidden', now.getHours() >= cutoff);
    this.removeAttribute('hidden');
  }
}

/* -------------------------------------------------------------------------- */
/* 3. Carrousel                                                                */
/* -------------------------------------------------------------------------- */

class LioraSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
    this.autoplayMs = Number(this.dataset.autoplay || 0);

    this.prev?.addEventListener('click', () => this.scrollByCard(-1));
    this.next?.addEventListener('click', () => this.scrollByCard(1));

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.restart();
      });
    });

    this.track.addEventListener('scroll', () => this.syncControls(), { passive: true });
    this.resizeHandler = () => this.syncControls();
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    // Le defilement s'interrompt des que la personne regarde ou manipule le bloc.
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.track.addEventListener('pointerdown', () => this.stop());

    this.visibilityHandler = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.syncControls();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    window.removeEventListener('resize', this.resizeHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  get slideCount() {
    return this.track.children.length;
  }

  get slideSize() {
    const first = this.track.querySelector(':scope > *');
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
  }

  get currentIndex() {
    const size = this.slideSize;
    return size > 0 ? Math.round(this.track.scrollLeft / size) : 0;
  }

  goTo(index) {
    this.track.scrollTo({
      left: index * this.slideSize,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  scrollByCard(direction) {
    this.track.scrollBy({
      left: this.slideSize * direction,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  advance() {
    const next = this.currentIndex + 1;
    this.goTo(next >= this.slideCount ? 0 : next);
  }

  start() {
    if (!this.autoplayMs || prefersReducedMotion.matches || this.slideCount < 2 || document.hidden) return;
    this.stop();
    this.timer = setInterval(() => this.advance(), this.autoplayMs);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  syncControls() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd || max <= 0;
    this.toggleAttribute('data-static', max <= 0);

    if (this.dots.length) {
      const active = this.currentIndex;
      this.dots.forEach((dot, index) => {
        if (index === active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }
}

/* -------------------------------------------------------------------------- */
/* 4. Caracteres gravables                                                     */
/* -------------------------------------------------------------------------- */

/**
 * La machine ne grave que des lettres, des chiffres et une poignee de signes.
 * Tout le reste (emoji, symboles, ponctuation exotique) est retire a la saisie
 * plutot que refuse au moment de valider le panier.
 */
const NOT_ENGRAVABLE = /[^\p{L}\p{M}\p{N} '’.\-&]/gu;
const BLOCK_HINT_MS = 2600;

function filterEngravableInput(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('perso__input')) return;

  const original = input.value;
  const cleaned = original.replace(NOT_ENGRAVABLE, '');
  if (cleaned === original) return;

  const removed = original.length - cleaned.length;
  const caret = Math.max(0, (input.selectionStart ?? original.length) - removed);

  input.value = cleaned;
  input.setSelectionRange(caret, caret);

  const field = input.closest('.perso__field');
  if (!field) return;

  field.setAttribute('data-perso-blocked', 'true');
  clearTimeout(field.lioraBlockTimer);
  field.lioraBlockTimer = setTimeout(() => field.removeAttribute('data-perso-blocked'), BLOCK_HINT_MS);
}

// Phase de capture : on nettoie avant que le composant de personnalisation
// ne lise la valeur pour son compteur et son apercu.
document.addEventListener('input', filterEngravableInput, true);

/* -------------------------------------------------------------------------- */
/* 5. Description repliee, frappee au depliage                                 */
/* -------------------------------------------------------------------------- */

const TYPING_MAX_MS = 2500;

/**
 * Seuls des elements de bloc sont candidats au masquage quand ils se vident.
 * Les editeurs de texte enrichi enveloppent souvent les espaces dans des
 * <span> </span> : les masquer comme « vides » collerait les mots entre eux.
 */
const COLLAPSIBLE_BLOCKS = 'p, div, li, ul, ol, h1, h2, h3, h4, h5, h6, blockquote, figure';

/**
 * Le HTML complet est rendu par le serveur. On ne raccourcit que le contenu des
 * noeuds de texte : les balises restent en place, donc gras, italiques et liens
 * sont identiques replies ou deplies.
 *
 * Le depliage se fait caractere par caractere ; le repliage est instantane, une
 * animation inverse n'apportant rien a la lecture.
 */
class LioraReadMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-readmore-body]');
    this.toggle = this.querySelector('[data-readmore-toggle]');
    if (!this.body || !this.toggle) return;

    this.limit = Number(this.dataset.limit || 180);
    this.speed = Number(this.dataset.speed || 12);
    this.moreLabel = this.dataset.moreLabel || 'Lire la suite';
    this.lessLabel = this.dataset.lessLabel || 'Réduire';

    // Les noeuds d'espaces entre balises ne comptent pas dans la longueur :
    // ils resteraient invisibles a la frappe tout en decalant le compte.
    this.chunks = [];
    this.total = 0;

    const walker = document.createTreeWalker(this.body, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const full = node.nodeValue ?? '';
      const counts = /\S/.test(full);
      this.chunks.push({ node, full, counts });
      if (counts) this.total += full.length;
      node = walker.nextNode();
    }

    // Un paragraphe vide garderait sa hauteur de ligne : on le sort du flux.
    // Restreint aux blocs, jamais aux elements en ligne.
    this.blocks = Array.from(this.body.querySelectorAll(COLLAPSIBLE_BLOCKS));

    if (this.total <= this.limit) {
      this.toggle.hidden = true;
      this.body.classList.remove('liora-pitch__lede--clamped');
      return;
    }

    this.visible = this.limit;
    this.paint(this.visible);

    // Le masquage CSS n'avait qu'un role de premier affichage.
    this.body.classList.remove('liora-pitch__lede--clamped');

    this.toggle.addEventListener('click', () => this.switch());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
  }

  /** Rend les `count` premiers caracteres visibles, le reste vide. */
  paint(count) {
    let left = count;

    for (const chunk of this.chunks) {
      if (!chunk.counts) {
        chunk.node.nodeValue = chunk.full;
        continue;
      }
      if (left >= chunk.full.length) {
        chunk.node.nodeValue = chunk.full;
        left -= chunk.full.length;
      } else {
        chunk.node.nodeValue = chunk.full.slice(0, Math.max(0, left));
        left = 0;
      }
    }

    for (const element of this.blocks) {
      element.toggleAttribute('data-liora-empty', element.textContent.trim() === '');
    }
  }

  switch() {
    const expanding = this.dataset.expanded !== 'true';

    this.dataset.expanded = String(expanding);
    this.toggle.setAttribute('aria-expanded', String(expanding));
    this.toggle.textContent = expanding ? this.lessLabel : this.moreLabel;

    if (expanding) {
      this.typeTo(this.total);
      return;
    }

    // Repli : retour immediat a l'extrait.
    cancelAnimationFrame(this.frame);
    this.visible = this.limit;
    this.paint(this.limit);
  }

  typeTo(target) {
    cancelAnimationFrame(this.frame);

    const from = this.visible;
    const delta = target - from;
    if (delta <= 0) return;

    if (prefersReducedMotion.matches) {
      this.visible = target;
      this.paint(target);
      return;
    }

    // Plafond de duree : une description de 2 000 signes ne doit pas se taper
    // pendant vingt secondes.
    const duration = Math.min(delta * this.speed, TYPING_MAX_MS);

    // Vitesse 0 : depliage instantane. Sans cette sortie, la boucle diviserait
    // par zero et le premier calcul de progression rendrait NaN, ce qui vide
    // tous les noeuds de texte le temps d'une image.
    if (!(duration > 0)) {
      this.visible = target;
      this.paint(target);
      return;
    }
    const startedAt = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const count = Math.round(from + delta * progress);

      if (count !== this.visible) {
        this.visible = count;
        this.paint(count);
      }

      if (progress < 1) this.frame = requestAnimationFrame(step);
    };

    this.frame = requestAnimationFrame(step);
  }
}

/* -------------------------------------------------------------------------- */
/* 6. <morrila-bundle> : le format choisi pilote la quantite ajoutee au panier */
/* -------------------------------------------------------------------------- */

/**
 * Le champ cache `quantity` est relie au formulaire d'achat par attribut form :
 * FormData le ramasse a l'envoi sans qu'on touche au bloc natif. On previent en
 * plus la barre collante, qui tient son propre compteur.
 */
class MorrilaBundle extends HTMLElement {
  connectedCallback() {
    this.field = this.querySelector('[data-mrl-quantity]');
    this.radios = Array.from(this.querySelectorAll('[data-mrl-radio]'));
    if (!this.field || !this.radios.length) return;

    this.onChange = () => this.sync();
    this.addEventListener('change', this.onChange);

    // L'etat rendu par le serveur fait foi : on realigne le champ cache sur la
    // carte cochee plutot que de supposer qu'ils sont deja d'accord.
    this.sync({ silent: true });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  sync({ silent = false } = {}) {
    const selected = this.radios.find((radio) => radio.checked) ?? this.radios[0];
    const quantity = Number(selected?.value) || 1;

    if (this.field.value === String(quantity)) return;
    this.field.value = String(quantity);
    if (silent) return;

    // Meme nom et meme forme de detail que QuantitySelectorUpdateEvent, que
    // sticky-add-to-cart.js ecoute sur document pour son « (2) ».
    const update = new Event('quantity-selector:update', { bubbles: true });
    update.detail = { quantity, cartLine: undefined };
    this.dispatchEvent(update);
  }
}

/* -------------------------------------------------------------------------- */
/* 7. <liora-foot-cols> : colonnes de pied de page, repliables en mobile      */
/* -------------------------------------------------------------------------- */

/**
 * Les colonnes sont rendues ouvertes pour rester lisibles sans JavaScript.
 * On les referme sous 750 px, ou le pied de page doit tenir en une poignee de
 * lignes, et on les rouvre au-dela.
 */
class LioraFootCols extends HTMLElement {
  connectedCallback() {
    this.cols = Array.from(this.querySelectorAll('details'));
    if (!this.cols.length) return;

    this.mq = window.matchMedia('(max-width: 749px)');
    this.apply = () => {
      const mobile = this.mq.matches;
      for (const col of this.cols) col.open = !mobile;
    };
    this.apply();
    this.mq.addEventListener('change', this.apply);
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.apply);
  }
}

/* -------------------------------------------------------------------------- */
/* Enregistrement                                                              */
/* -------------------------------------------------------------------------- */

if (!customElements.get('liora-eta')) customElements.define('liora-eta', LioraEta);
if (!customElements.get('liora-slider')) customElements.define('liora-slider', LioraSlider);
if (!customElements.get('liora-readmore')) customElements.define('liora-readmore', LioraReadMore);
if (!customElements.get('morrila-bundle')) customElements.define('morrila-bundle', MorrilaBundle);
if (!customElements.get('liora-foot-cols')) customElements.define('liora-foot-cols', LioraFootCols);

initReveals();
document.addEventListener('shopify:section:load', (event) => initReveals(event.target));
document.addEventListener('shopify:block:select', (event) => initReveals(event.target));
